/* oxlint-disable react-doctor/rendering-hydration-mismatch-time */
import Tooltip from 'components/@extended/Tooltip';
import { buttonStyle } from 'components/buttons/buttonStyle';
import { CREATE_DIALOG_OTHER_ACTION, CreateDialogModalFrame } from 'components/create-dialog/CreateDialogModalFrame';
import { CreateDialogSection } from 'components/create-dialog/CreateDialogSection';
import { createRecord } from 'components/create-dialog/recordTypes';
import FeatureFlagged from 'components/featureflag/FeatureFlagged.tsx';
import CheckboxInput from 'components/forms/inputs/rhf-inputs/CheckboxInput.tsx';
import DateInput from 'components/forms/inputs/rhf-inputs/DateInput';
import DecimalInput from 'components/forms/inputs/rhf-inputs/DecimalInput.tsx';
import DynamicSelectInput from 'components/forms/inputs/rhf-inputs/DynamicSelectInput.tsx';
import SelectInput from 'components/forms/inputs/rhf-inputs/SelectInput';
import TextAreaInput from 'components/forms/inputs/rhf-inputs/TextAreaInput';
import { FormattedDate } from 'components/misc/FormattedDate';
import { ModalFooterSimple } from 'components/modals/ModalFooterSimple';
import { ModalHeader } from 'components/modals/ModalHeader';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import { Button } from 'components/ui/button';
import { ReactNode, useCallback, useMemo } from 'react';
import { Control, UseFormGetValues, UseFormHandleSubmit, UseFormSetValue, useWatch } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'store';
import { useLazyGetEmployeesListWithFiltersQuery } from 'store/api/employees.ts';
import { useLazyGetMattersListQuery } from 'store/api/matters.ts';
import { ClassifierData } from 'types/classifiers';
import { ExpenseDto } from 'types/expenses.ts';
import { FeatureFlag } from 'types/features.ts';
import { InvoiceMatterForm } from 'types/invoice-details';
import { EditIntentTypeCode, InvoiceData } from 'types/invoices.ts';
import { cn } from 'utils/cn';
import { getLabelWithDescription, getValidOptionsFromClassifier } from 'utils/formUtil';
import { t } from 'utils/getTranslations.ts';
import { renderMatterIndicators, renderMatterOption } from 'utils/matterOptionRenderer';
import { useHasPermissions } from 'utils/permissionUtil.ts';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags.ts';
import { paymentCurrencyInputClassName, usePaymentCurrencyMode } from 'utils/usePaymentCurrencyMode';
import { getInvoiceExpenseAmountPattern } from './invoiceExpenseAmountPattern';
import PaymentCurrencyNotice from '../../components/PaymentCurrencyNotice';

/** The sections the new design draws the form in: the expense's own fields, and what the invoice makes of it. */
const EXPENSE_SECTION = 'expenses.expense';
const INVOICE_SECTION = 'invoices.invoice';

interface Props {
    onClose: () => void;
    onSubmit: () => Promise<void>;
    control: Control<InvoiceMatterForm>;
    validateForm: UseFormHandleSubmit<InvoiceMatterForm>;
    setValue: UseFormSetValue<InvoiceMatterForm>;
    getValues: UseFormGetValues<InvoiceMatterForm>;
    index: number;
    invoice: InvoiceData;
    classifiers: Record<string, ClassifierData>;
    expense: ExpenseDto | null;
}

const InvoiceExpenseEditModal = (props: Props) => {
    const { onSubmit, onClose, control, validateForm, setValue, getValues, index, invoice, classifiers, expense } =
        props;
    const isExtraOptionsShown = useIsFeatureEnabled(FeatureFlag.INVOICE_EXPENSE_EXTRA_OPTIONS);
    const isNegativeAmountAllowed = useIsFeatureEnabled(FeatureFlag.EXPENSES_NEGATIVE_AMOUNT_ALLOWED);
    const { isPaymentCurrencyMode, displayCurrency } = usePaymentCurrencyMode(invoice);

    const matterIdList = useMemo(() => {
        return invoice?.invoiceMatters?.map((item) => item.matterId) || [];
    }, [invoice?.invoiceMatters]);

    const mattersListFilter = matterIdList.length > 0 ? `id=in=(${matterIdList.join(',')})` : undefined;

    const { buttonsDisabled: areButtonsDisabled } = useSelector((state) => state.buttons);

    const handleOnSave = () => {
        validateForm(async () => {
            try {
                await onSubmit();
                onClose();
            } catch (e) {
                console.error('onSubmit error: ', e);
            }
        })();
    };

    const handleIsNonBillableChange = useCallback(
        (checked: boolean) => {
            if (checked) {
                setValue(`invoiceExpenses.${index}.editIntentTypeCode`, EditIntentTypeCode.MARK_AS_NONBILLABLE);
            } else {
                setValue(`invoiceExpenses.${index}.editIntentTypeCode`, null);
            }
        },
        [setValue, index]
    );

    const [isNonBillable, matter] = useWatch({
        control,
        name: [`invoiceExpenses.${index}.isNonBillable`, `invoiceExpenses.${index}.matter`],
    });
    const isNewDesign = useIsNewDesign();
    const clientName = invoice.invoiceMatters?.find((item) => item.matterId === matter?.id)?.clientParty?.name;

    const handleResetToOriginal = useCallback(() => {
        setValue(`invoiceExpenses.${index}.description`, expense?.description || '');
        setValue(`invoiceExpenses.${index}.employee`, expense?.employee ?? null);
    }, [setValue, index, expense]);

    const hasPermissionToEnableMirroring = useHasPermissions(['UPDATE.INVOICES.TC_EXP_MIRROR']);

    const expenseId = getValues(`invoiceExpenses.${index}.id`);

    const matterField = (
        <DynamicSelectInput
            setValue={setValue}
            control={control}
            label="expenses.form.case-client"
            name={`invoiceExpenses.${index}.matter`}
            getOptionLabel="fullName"
            paginated={{
                fetch: useLazyGetMattersListQuery,
                searchField: ['fullName', 'clientParty.name'],
                filters: mattersListFilter,
                isEnabled: matterIdList.length > 0,
                sort: ['clientParty.name,asc', 'name,asc'],
            }}
            groupByField="clientParty.name"
            getOptionIdentifier="id"
            placeholder="expenses.form.select-case"
            placeholderOnFocus="expenses.form.search-case-by"
            getSelectedValueStartAdornment={renderMatterIndicators}
            customOptionRenderer={renderMatterOption}
            required
            disabled={areButtonsDisabled}
            // The case takes the row in Required, as the create dialog's case does.
            wrapperClassName={isNewDesign ? 'col-span-full' : undefined}
            bottomRightLabelComponent={
                // Inside the field, so the line follows it into the dialog's Required section.
                isNewDesign && clientName ? (
                    <div className="text-muted-foreground w-full text-xs">
                        {t('expenses.form.client')}: {clientName}
                    </div>
                ) : undefined
            }
        />
    );
    const expenseTypeField = (
        <SelectInput
            setValue={setValue}
            control={control}
            label="expenses.form.expense-type"
            name={`invoiceExpenses.${index}.expenseType`}
            options={
                (getValidOptionsFromClassifier(classifiers['EXPENSE_TYPE']) as {
                    label: string;
                    value: string;
                }[]) ?? []
            }
            getOptionLabel={getLabelWithDescription(classifiers['EXPENSE_TYPE'])}
            getOptionIdentifier="value"
            placeholder="expenses.form.expense-type"
            required
            disabled={areButtonsDisabled}
        />
    );
    const amountLabel = isNewDesign ? 'expenses.form.net-amount' : 'expenses.form.amount';
    const amountField = (
        <DecimalInput
            pattern={getInvoiceExpenseAmountPattern(isNegativeAmountAllowed)}
            name={`invoiceExpenses.${index}.expenseAmount`}
            control={control}
            label={amountLabel}
            placeholder={amountLabel}
            required
            disabled={isNonBillable || areButtonsDisabled}
            suffixComponent={displayCurrency}
        />
    );
    const descriptionField = (
        <TextAreaInput
            control={control}
            label="expenses.form.details-of-expense"
            name={`invoiceExpenses.${index}.description`}
            disabled={areButtonsDisabled}
        />
    );
    const nonBillableField = (
        <CheckboxInput
            control={control}
            name={`invoiceExpenses.${index}.isNonBillable`}
            label="expenses.form.is-non-billable"
            onChange={handleIsNonBillableChange}
            disabled={areButtonsDisabled}
        />
    );
    const employeeField = (
        <DynamicSelectInput
            setValue={setValue}
            control={control}
            label="expenses.form.added-by"
            name={`invoiceExpenses.${index}.employee`}
            placeholder="expenses.form.select-employee"
            paginated={{
                fetch: useLazyGetEmployeesListWithFiltersQuery,
                searchField: 'fullName',
            }}
            getOptionLabel="name"
            getOptionIdentifier="id"
            required
            disabled={areButtonsDisabled}
        />
    );
    const dateField = (
        <DateInput
            maxDate={new Date()}
            control={control}
            label="date"
            name={`invoiceExpenses.${index}.expenseDate`}
            required
            disabled={areButtonsDisabled}
        />
    );
    const mirroredCheckbox = (
        <CheckboxInput
            control={control}
            label={'invoices.details.table.is-mirrored-expense-label'}
            name={`invoiceExpenses.${index}.isMirrored`}
            keepCheckbox
            disabled={!hasPermissionToEnableMirroring || areButtonsDisabled}
        />
    );
    const hiddenCheckbox = (
        <CheckboxInput
            control={control}
            label={'invoices.details.table.is-hidden-expense-label'}
            name={`invoiceExpenses.${index}.isHidden`}
            keepCheckbox
            disabled={!hasPermissionToEnableMirroring || areButtonsDisabled}
        />
    );

    if (isNewDesign) {
        const record = createRecord('expense');
        return (
            <CreateDialogModalFrame
                icon={record.icon}
                tileClass={record.tileClass}
                title={
                    <FormattedMessage
                        id="create-dialog.edit-title"
                        values={{ record: t('create-dialog.record.expense') }}
                    />
                }
                chip={
                    <>
                        <FormattedDate value={getValues(`invoiceExpenses.${index}.expenseDate`)} /> ·{' '}
                        {getValues(`invoiceExpenses.${index}.employee`)?.name}
                    </>
                }
                notice={isPaymentCurrencyMode ? <PaymentCurrencyNotice currency={displayCurrency} /> : null}
                openSection={INVOICE_SECTION}
                caption={
                    <FormattedMessage
                        id="invoices.details.table.reset-expense-caption"
                        values={{ b: (chunks: ReactNode) => <strong className="font-medium">{chunks}</strong> }}
                    />
                }
                onCancel={onClose}
                cancelDisabled={areButtonsDisabled}
                otherActions={
                    <Button
                        {...CREATE_DIALOG_OTHER_ACTION}
                        onClick={handleResetToOriginal}
                        disabled={areButtonsDisabled}
                    >
                        <FormattedMessage id="invoices.details.table.reset" />
                    </Button>
                }
                mainAction={{ label: t('save'), onClick: handleOnSave, disabled: areButtonsDisabled }}
            >
                {/* Every field the expense form has, in its order; the required ones are drawn in Required. */}
                <CreateDialogSection title={EXPENSE_SECTION}>
                    {matterField}
                    {expenseTypeField}
                    {amountField}
                    {employeeField}
                    {dateField}
                    {descriptionField}
                    {nonBillableField}
                </CreateDialogSection>
                <CreateDialogSection title={INVOICE_SECTION}>
                    <FeatureFlagged action="show" flag={FeatureFlag.INVOICE_EXPENSE_EXTRA_OPTIONS}>
                        <>
                            {mirroredCheckbox}
                            <FeatureFlagged action="show" flag={FeatureFlag.HIDE_TIMECARD_EXPENSE_FROM_INVOICE}>
                                {hiddenCheckbox}
                            </FeatureFlagged>
                        </>
                    </FeatureFlagged>
                </CreateDialogSection>
            </CreateDialogModalFrame>
        );
    }

    return (
        <>
            <ModalHeader
                title={t('invoices.details.invoice-expense')}
                onCancel={onClose}
                subtitle={`(ID: ${expenseId})`}
            />
            <div className="h-full overflow-y-auto p-6 max-sm:px-4">
                {isPaymentCurrencyMode && <PaymentCurrencyNotice currency={displayCurrency} className="mb-4" />}
                {/* A 12-column grid with 16px gutters; a field tucked closer under the one above takes a
                    negative top margin. */}
                <div className="grid grid-cols-12 gap-4">
                    <div className="col-span-12">{matterField}</div>
                    <div className="col-span-12">{expenseTypeField}</div>
                    <div className={cn('col-span-6', isPaymentCurrencyMode && paymentCurrencyInputClassName)}>
                        {amountField}
                    </div>
                    <div className="col-span-12">{descriptionField}</div>
                    <div className="col-span-12">{nonBillableField}</div>
                    <div className="col-span-12">{employeeField}</div>
                    <div className="col-span-12">{dateField}</div>
                    <FeatureFlagged action="show" flag={FeatureFlag.INVOICE_EXPENSE_EXTRA_OPTIONS}>
                        <>
                            <div className="col-span-12 -mt-2">{mirroredCheckbox}</div>
                            <FeatureFlagged action="show" flag={FeatureFlag.HIDE_TIMECARD_EXPENSE_FROM_INVOICE}>
                                <div className="col-span-12 -mt-4">{hiddenCheckbox}</div>
                            </FeatureFlagged>
                        </>
                    </FeatureFlagged>
                    <div className={cn('col-span-12', isExtraOptionsShown && '-mt-2')}>
                        <hr className="border-divider" />
                    </div>
                    <div className="col-span-6">
                        <Tooltip text="invoices.details.table.reset-expense-tooltip">
                            <Button
                                {...buttonStyle({ variant: 'outlined', color: 'primary' })}
                                onClick={handleResetToOriginal}
                                disabled={areButtonsDisabled}
                            >
                                <FormattedMessage id="invoices.details.table.reset" />
                            </Button>
                        </Tooltip>
                    </div>
                </div>
            </div>
            <ModalFooterSimple
                buttons={[
                    <Button
                        key="cancel"
                        {...buttonStyle({ variant: 'outlined', color: 'primary' })}
                        onClick={onClose}
                        disabled={areButtonsDisabled}
                    >
                        {t('cancel')}
                    </Button>,
                    <Button
                        key="update-expense"
                        {...buttonStyle({ variant: 'contained', color: 'secondary' })}
                        onClick={handleOnSave}
                        disabled={areButtonsDisabled}
                    >
                        {t('save')}
                    </Button>,
                ]}
            />
        </>
    );
};

export default InvoiceExpenseEditModal;
